import { useContext, useState, useMemo, useEffect } from "react";
import { DiscoveryContext, useIsSelectedSection } from "../discovery-context";
import { filterUniqueDomains } from "./Sources";
import { tab } from "../BrandScore/BrandScore";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import ReTableView from "../../Dashboard/common/ReTableView"
import { transformSourcesResponse, transformOldSourcesResponse } from "../../../utils/helpers"
import { downloadExcelV2, prepareSourceDataForExcel } from "../../../utils/monitoring-helpers";
import { Button } from "@mui/material";
import { OVERVIEW_LLM_LABELS } from "../../../utils/constant"
import classes from "./Sources.module.css";
import { ENTITY_LABELS } from "../../../utils/labelMapping";

const COMPARISON_OPTIONS = [
  { value: "LLM", label: "Compare by LLM" },
  { value: "PERSONA", label: "Compare by Persona" },
  { value: "MARKET", label: "Compare by Market" },
  { value: "CATEGORY", label: "Compare by Category" },
];

const LABEL_MAPPING = {
  LLM: "llm",
  PERSONA: "persona",
  MARKET: "market",
  CATEGORY: "category",
};

// Placeholder path keys so non-LLM modes can reuse the existing transform functions
const _CAT = "__cat__";
const _MKT = "__mkt__";

const SourcesOverview = ({
  category,
  isCategoryOverview = false,
  externalFilter = null,
  externalComparisons = null,
  onExternalFilterChange = null,
  onExternalComparisonsChange = null,
  showCompareControls = true,
  ctx = null,
}) => {
  const _defaultCtx = useContext(DiscoveryContext);
  const discoveryCtx = ctx || _defaultCtx;

  const [sourceFilter, setSourceFilter] = useState({ value: "LLM", label: "Compare by LLM" });
  const [selectedComparisons, setSelectedComparisons] = useState(discoveryCtx.llm);
  const [selectedBrandSources, setSelectedBrandSources] = useState([]);

  const isExternallyControlled = isCategoryOverview && externalFilter != null;
  const activeFilter = isExternallyControlled ? externalFilter : sourceFilter;
  const activeComparisons = isExternallyControlled ? (externalComparisons ?? []) : selectedComparisons;

  const discoveryType = discoveryCtx.type || "brand";
  const selectedCategory = isCategoryOverview ? category : discoveryCtx.state.selectedCategory;
  const selectedMarket = discoveryCtx.state.selectedMarket;
  const selectedPersona = discoveryCtx.state.selectedPersona;
  const sources = discoveryCtx?.sources ?? [];
  const showOldSourcesRecord = discoveryCtx.state.sourceValidationStatus === "PENDING";

  // Fixed LLM used when comparison mode is not LLM
  const fixedLLM = discoveryCtx.state.selectedLLM;

  const personaTitles = useMemo(
    () =>
      (discoveryCtx.persona || [])
        .map((p) => (typeof p === "object" && p?.title ? p.title : p))
        .filter(Boolean),
    [discoveryCtx.persona]
  );

  const allMarkets = discoveryCtx.markets || [];
  const allCategories = discoveryCtx.category || [];

  const getDimensionItems = (filterValue) => {
    switch (filterValue) {
      case "LLM": return discoveryCtx.llm;
      case "PERSONA": return personaTitles;
      case "MARKET": return allMarkets;
      case "CATEGORY": return allCategories;
      default: return discoveryCtx.llm;
    }
  };

  // Sync when context LLM list changes (only relevant in LLM mode)
  useEffect(() => {
    if (sourceFilter.value === "LLM") {
      setSelectedComparisons(discoveryCtx.llm);
    }
  }, [discoveryCtx.llm]);

  // Reset comparison selection when filter mode changes
  const handleFilterChange = (newFilter) => {
    if (isExternallyControlled) {
      onExternalFilterChange?.(newFilter);
      onExternalComparisonsChange?.(getDimensionItems(newFilter.value));
    } else {
      setSourceFilter(newFilter);
      setSelectedComparisons(getDimensionItems(newFilter.value));
    }
  };

  const isLLMMode = activeFilter.value === "LLM";

  const pivotKeys = activeComparisons;


  const allMetricsKeys = (() => {
    const acc = [];
    const addKeys = (obj) => {
      Object.keys(obj || {}).forEach((key) => {
        if (!acc.includes(key)) acc.push(key);
      });
    };

    if (isLLMMode) {
      pivotKeys.forEach((llmKey) => {
        addKeys(sources?.[llmKey]?.[selectedCategory]?.[selectedMarket]?.[selectedPersona]);
      });
    } else if (activeFilter.value === "PERSONA") {
      personaTitles.forEach((p) => {
        addKeys(sources?.[fixedLLM]?.[selectedCategory]?.[selectedMarket]?.[p]);
      });
    } else if (activeFilter.value === "MARKET") {
      allMarkets.forEach((m) => {
        addKeys(sources?.[fixedLLM]?.[selectedCategory]?.[m]?.[selectedPersona]);
      });
    } else if (activeFilter.value === "CATEGORY") {
      allCategories.forEach((c) => {
        addKeys(sources?.[fixedLLM]?.[c]?.[selectedMarket]?.[selectedPersona]);
      });
    }
    return acc;
  })();

  const dropdownOptions = allMetricsKeys.map((key) => ({ value: key, label: key }));

  useEffect(() => {
    setSelectedBrandSources(dropdownOptions.map((o) => o.value));
  }, [JSON.stringify(dropdownOptions)]);


  const effectiveSelectedBrandSources =
    selectedBrandSources.length > 0 ? selectedBrandSources : allMetricsKeys;

  const sourcesData = {};

  pivotKeys.forEach((pivotKey) => {
    let rawSources;
    if (isLLMMode) {
      rawSources =
        sources?.[pivotKey]?.[selectedCategory]?.[selectedMarket]?.[selectedPersona] || {};
    } else if (activeFilter.value === "PERSONA") {
      rawSources =
        sources?.[fixedLLM]?.[selectedCategory]?.[selectedMarket]?.[pivotKey] || {};
    } else if (activeFilter.value === "MARKET") {
      rawSources =
        sources?.[fixedLLM]?.[selectedCategory]?.[pivotKey]?.[selectedPersona] || {};
    } else if (activeFilter.value === "CATEGORY") {
      rawSources =
        sources?.[fixedLLM]?.[pivotKey]?.[selectedMarket]?.[selectedPersona] || {};
    }

    let filteredData;
    if (showOldSourcesRecord) {
      filteredData = effectiveSelectedBrandSources.reduce((acc, key) => {
        if (rawSources[key]) {
          const sourceObj = rawSources[key];
          Object.keys(sourceObj).forEach((url) => {
            acc[url] = (acc[url] || 0) + sourceObj[url];
          });
        }
        return acc;
      }, {});
    } else {
      filteredData = effectiveSelectedBrandSources.reduce((acc, key) => {
        if (rawSources[key]) {
          acc.push(...(Array.isArray(rawSources[key]) ? rawSources[key] : [rawSources[key]]));
        }
        return acc;
      }, []);
    }

    if (!sourcesData[pivotKey]) sourcesData[pivotKey] = {};
    if (isLLMMode) {
      if (!sourcesData[pivotKey][selectedCategory]) sourcesData[pivotKey][selectedCategory] = {};
      if (!sourcesData[pivotKey][selectedCategory][selectedMarket])
        sourcesData[pivotKey][selectedCategory][selectedMarket] = {};
      sourcesData[pivotKey][selectedCategory][selectedMarket][selectedPersona] = filteredData;
    } else {
      if (!sourcesData[pivotKey][_CAT]) sourcesData[pivotKey][_CAT] = {};
      sourcesData[pivotKey][_CAT][_MKT] = filteredData;
    }
  });

  // Transform function args vary by mode
  const [tCat, tMkt, tPersona] = isLLMMode
    ? [selectedCategory, selectedMarket, selectedPersona]
    : [_CAT, _MKT, null];

  const selectedInput = {
    brand: discoveryCtx.entity,
    category: selectedCategory,
    llm: discoveryCtx.state.selectedLLM,
    market: selectedMarket,
    persona: selectedPersona,
    product: discoveryCtx.type == "product" ? true : false,
  };

  const effectiveSourceFilter = activeFilter;

  const data = useMemo(() => {
    if (showOldSourcesRecord) {
      return transformOldSourcesResponse(sourcesData, pivotKeys, tCat, tMkt, tPersona);
    } else {
      return transformSourcesResponse(sourcesData, pivotKeys, tCat, tMkt, tPersona);
    }
  }, [sourcesData, pivotKeys, tCat, tMkt, tPersona]);

  const id = "Sources_Overview";
  const uniqueKey = `${tab.brandOverview}_${id}_${discoveryCtx?.state?.selectedCategory}_${discoveryCtx?.state?.selectedMarket}_${discoveryCtx?.state?.selectedPersona}`;
  const { checked } = useIsSelectedSection({ id, tab: tab.brandOverview, uniqueKey });
  const isFinalAudit =
    discoveryCtx?.state?.finalTransactionId === discoveryCtx?.state?.transactionId;
  const isSourceValidating =
    discoveryCtx.state?.sourceValidationStatus === "RUNNING" &&
    ["COMPLETED", "RETRY"].includes(discoveryCtx.state?.transactionStatus) &&
    isFinalAudit;

  // Options for the comparison-dimension MultiSelect
  const comparisonDimensionOptions = getDimensionItems(activeFilter.value).map((item) => ({
    value: item,
    name: activeFilter.value === "LLM" ? (OVERVIEW_LLM_LABELS[item] || item) : item,
  }));

  // Category tabs omit "Compare by Category" since the category is already fixed
  const activeComparisonOptions = isCategoryOverview
    ? COMPARISON_OPTIONS.filter((o) => o.value !== "CATEGORY")
    : COMPARISON_OPTIONS;

  // Shared dropdown pair — rendered in both brand-overview and category-tab layouts
  const compareControlsJSX = (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel sx={{ fontSize: 13 }}>Compare Across</InputLabel>
        <Select
          value={activeFilter.value}
          label="Compare Across"
          onChange={(e) => {
            const newFilter = activeComparisonOptions.find((o) => o.value === e.target.value);
            if (newFilter) handleFilterChange(newFilter);
          }}
          sx={{
            minHeight: "40px",
            "& .MuiSelect-select": {
              paddingTop: "6px",
              paddingBottom: "6px",
              minHeight: "28px",
            },
          }}
        >
          {activeComparisonOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel sx={{ fontSize: 13 }}>Compare Items</InputLabel>
        <Select
          multiple
          value={activeComparisons}
          label="Compare Items"
          onChange={(e) => {
            const val = e.target.value;
            setSelectedComparisons(Array.isArray(val) ? val : [val]);
          }}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
              {selected.map((val) => {
                const displayName =
                  sourceFilter.value === "LLM"
                    ? OVERVIEW_LLM_LABELS[val] || val
                    : val;
                return (
                  <Chip
                    key={val}
                    label={displayName}
                    size="small"
                    onDelete={(e) => {
                      e.stopPropagation();
                      setSelectedComparisons(selectedComparisons.filter((v) => v !== val));
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      height: "auto",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: 1.3,
                        paddingTop: "4px",
                        paddingBottom: "4px",
                        paddingRight: "4px",
                      },
                      "& .MuiChip-deleteIcon": {
                        fontSize: "18px",
                        margin: "0 4px 0 -2px",
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
          sx={{
            minHeight: "40px",
            "& .MuiSelect-select": {
              paddingTop: "6px",
              paddingBottom: "6px",
              display: "flex",
              alignItems: "center",
              minHeight: "28px",
            },
          }}
        >
          {comparisonDimensionOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Checkbox checked={selectedComparisons.includes(opt.value)} />
              <ListItemText primary={opt.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );

  return (
    <>
      <br />
      {!isCategoryOverview && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <h5 style={{ marginRight: "8px" }}>Sources Overview</h5>
              {discoveryCtx.state.showReportCheckbox && (
                <Checkbox
                  checked={checked}
                  onChange={() => {
                    discoveryCtx?.clickComponentCheckbox({
                      uniqueKey,
                      filter: {
                        id: id,
                        title: "Sources Overview",
                        tab: tab.brandOverview,
                        uniqueKey,
                        llm: null,
                      },
                      checked,
                      id,
                    });
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                />
              )}
            </div>
            {compareControlsJSX}
          </div>
          <hr />
        </>
      )}
      {isCategoryOverview && !isExternallyControlled && showCompareControls && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {compareControlsJSX}
          </div>
          <hr />
        </>
      )}
      <Button
        onClick={() => {
          const preparedData = prepareSourceDataForExcel(
            data,
            selectedInput,
            LABEL_MAPPING,
            effectiveSourceFilter
          );
          downloadExcelV2(preparedData, LABEL_MAPPING[effectiveSourceFilter?.value]);
        }}
      >
        Export
      </Button>
      {isSourceValidating && (
        <div className={classes.validationMessage}>
          <div>
            <span style={{ fontSize: "18px", marginRight: "8px" }}>⏳</span>
            Source validation is still in progress
          </div>
        </div>
      )}
      <div id={id}>
        <ReTableView
          type={`${discoveryType === "brand" ? ENTITY_LABELS.brand : ENTITY_LABELS.product}`}
          data={data}
          isValidated={!showOldSourcesRecord}
          isOverview={true}
          dropdownOptions={dropdownOptions}
          selectedDropdownValues={effectiveSelectedBrandSources}
          onDropdownChange={(newVal) =>
            setSelectedBrandSources(
              Array.isArray(newVal)
                ? newVal
                : newVal?.target
                ? newVal.target.value
                : selectedBrandSources
            )
          }
          selectedInput={selectedInput}
        />
      </div>
    </>
  );
};

export default SourcesOverview;

export const SOURCES_CATEGORY_COMPARISON_OPTIONS = COMPARISON_OPTIONS.filter(
  (o) => o.value !== "CATEGORY"
);

export const getSourceDimensionOptions = (filterValue, discoveryCtx) => {
  const { llm = [], persona = [], markets = [], category = [] } = discoveryCtx;
  const personaTitles = persona
    .map((p) => (typeof p === "object" && p?.title ? p.title : p))
    .filter(Boolean);
  const items =
    filterValue === "LLM" ? llm :
    filterValue === "PERSONA" ? personaTitles :
    filterValue === "MARKET" ? markets : category;
  return items.map((item) => ({
    value: item,
    name: filterValue === "LLM" ? (OVERVIEW_LLM_LABELS[item] || item) : item,
  }));
};

export const SourceCompareControls = ({
  sourceFilter,
  selectedComparisons,
  comparisonOptions,
  dimensionOptions,
  onFilterChange,
  onComparisonsChange,
}) => (
  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel sx={{ fontSize: 13 }}>Compare Across</InputLabel>
      <Select
        value={sourceFilter.value}
        label="Compare Across"
        onChange={(e) => {
          const newFilter = comparisonOptions.find((o) => o.value === e.target.value);
          if (newFilter) onFilterChange(newFilter);
        }}
        sx={{
          minHeight: "40px",
          "& .MuiSelect-select": { paddingTop: "6px", paddingBottom: "6px", minHeight: "28px" },
        }}
      >
        {comparisonOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel sx={{ fontSize: 13 }}>Compare Items</InputLabel>
      <Select
        multiple
        value={selectedComparisons}
        label="Compare Items"
        onChange={(e) => {
          const val = e.target.value;
          onComparisonsChange(Array.isArray(val) ? val : [val]);
        }}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
            {selected.map((val) => {
              const opt = dimensionOptions.find((o) => o.value === val);
              return (
                <Chip
                  key={val}
                  label={opt ? opt.name : val}
                  size="small"
                  onDelete={(e) => {
                    e.stopPropagation();
                    onComparisonsChange(selectedComparisons.filter((v) => v !== val));
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.3,
                      paddingTop: "4px",
                      paddingBottom: "4px",
                      paddingRight: "4px",
                    },
                    "& .MuiChip-deleteIcon": { fontSize: "18px", margin: "0 4px 0 -2px" },
                  }}
                />
              );
            })}
          </Box>
        )}
        sx={{
          minHeight: "40px",
          "& .MuiSelect-select": {
            paddingTop: "6px",
            paddingBottom: "6px",
            display: "flex",
            alignItems: "center",
            minHeight: "28px",
          },
        }}
      >
        {dimensionOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Checkbox checked={selectedComparisons.includes(opt.value)} />
            <ListItemText primary={opt.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </div>
);

export function formatDomainData(mapArr, data, filterArr, market, mode = "count") {
  let formatted = {};

  mapArr.forEach((key) => {
    if (!data[key]) return;
    let propertyArr = Object.entries(data[key]);
    if (!propertyArr.length) return;

    propertyArr.forEach(([name, obj]) => {
      if (!filterArr.includes(name)) return;

      let marketUrls = obj?.[market] || [];

      let domains = filterUniqueDomains(marketUrls);

      let reduceObj = domains.reduce((acc, d) => {
        if (mode === "count") {
          acc[d.src] = d?.urls?.length ?? 0;
        } else if (mode === "urls") {
          acc[d.src] = (d?.urls ?? []).map((url) => ({
            url,
            http_status: "200", // hardcoded
          }));
        }
        return acc;
      }, {});

      if (mode === "count") {
        // keep LLM key wrapper
        formatted[key] = { ...formatted[key], ...reduceObj };
      } else {
        // flatten (no LLM key)
        formatted = { ...formatted, ...reduceObj };
      }
    });
  });

  return formatted;
}

export function extractDomain(mapArr, data, filterArr, market, mode = "count") {
  let formatted = {};

  mapArr.forEach((llmKey) => {
    if (!data[llmKey]) return;

    let propertyArr = Object.entries(data[llmKey]);
    if (!propertyArr.length) return;

    propertyArr.forEach(([categoryName, obj]) => {
      if (!filterArr.includes(categoryName)) return;

      let marketUrls = obj?.[market] || [];
      let domains = filterUniqueDomains(marketUrls);

      domains.forEach((d) => {
        const domain = d.src;

        if (mode === "count") {
          if (!formatted[domain]) {
            formatted[domain] = { name: domain };
          }
          formatted[domain][llmKey] = d.urls.length;
        } else if (mode === "urls") {
          if (!formatted[domain]) {
            formatted[domain] = [];
          }
          // add urls with LLM info
          const urls = (d?.urls ?? []).map((url) => ({
            url,
            http_status: "200",
            llm: llmKey
          }));
          formatted[domain].push(...urls);
        }
      });
    });
  });

  if (mode === "count") {
    // ensure all LLM keys exist in each object
    return Object.values(formatted).map((entry) => {
      mapArr.forEach((llmKey) => {
        if (!(llmKey in entry)) {
          entry[llmKey] = 0;
        }
      });
      return entry;
    });
  } else {
    // mode = "urls", return dictionary domain -> urls[]
    return formatted;
  }
}


